// Which actions a quote offers, and the rules the Quote Hub and the Quotes list row share (Session ARP).
//
// The Director's ruling of 25 September 2026: a quote row collapses to ONE button plus a "..." menu, and
// the quote page (the Quote Hub, Session AQW) carries everything. Before this file the two pages each
// decided for themselves which action applied to which status, and they had drifted: the page offered
// Edit on a draft only, Extend on an expired quote only and no Re-quote at all; it deleted an accepted
// quote without the force the row sent (so the server refused it); it offered Resend on `opened`, which
// the resend route refuses; and its Send confirm said "the customer" where the row named the address.
// Now both pages read the rules below, so a quote cannot offer an action in one place and not the other.
//
// Pure, so it can be TESTED rather than trusted. Nothing here calls the server: each page still calls
// the same api.quotes.* method it always did, and quoteActionParity.test.tsx proves the two pages send
// the identical request for every action this session moved.
import type { QuoteDetail } from "./api";

// Every action a quote can offer anywhere in the office. The names are stable identifiers, not labels.
export type QuoteAction =
  | "send" // first send of a draft (POST /:id/send)
  | "resend" // re-email a quote awaiting the customer (POST /:id/resend)
  | "edit" // open it in the calculator to revise in place
  | "viewPdf" // the draft's office PDF (GET /:id/pdf)
  | "viewQuote" // the customer-facing accept page (/quote/:token)
  | "copyLink" // copy that accept link
  | "history" // the append-only version history
  | "duplicate" // an independent draft copy (POST /:id/duplicate)
  | "reviewFrequency" // the AAO frequency confirm gate, or the renewal's contract page (AIK)
  | "void" // cancel the Signable request (POST /:id/void)
  | "markLost" // record the deal lost, with a reason (POST /:id/lost)
  | "reopen" // a lost or voided quote back to a draft (POST /:id/reopen)
  | "extend" // push the validity forward (POST /:id/extend-validity)
  | "requote" // an expired quote back into the calculator
  | "delete"; // admin only (DELETE /:id)

// The fields the rules read. Both QuoteListItem and QuoteDetail satisfy it.
export interface QuoteActionSubject {
  status: string;
  token: string | null;
  renewalOfContractId?: string | null;
  frequencyChangePending?: boolean;
}

// The statuses a quote can be marked LOST from (mirrors the server's QUOTE_LOSTABLE_STATUSES). A
// signed quote is a live contract, an accepted quote is mid-signature, voided and lost are finished.
const LOSTABLE = new Set(["draft", "pending", "sent", "opened", "expired", "declined"]);

// Each status set below is the SERVER's own guard, not a preference: /resend refuses anything but
// pending and sent (so `opened`, a legacy import state, gets no Resend), /extend-validity takes pending,
// sent and expired, /void refuses only a signed quote.
export function quoteActions(q: QuoteActionSubject, opts: { isAdmin: boolean }): QuoteAction[] {
  const s = q.status;
  // A RENEWAL quotation is never revised in the calculator (the QUOTE REOPEN RULE, Session APW): the
  // calculator knows nothing of a renewal, and the create route's revise branch refuses it. So neither
  // Edit nor Re-quote is offered for one; the office acts on the contract it renews instead.
  const renewal = Boolean(q.renewalOfContractId);
  const live = s === "pending" || s === "sent";
  const hasLink = s !== "draft" && Boolean(q.token);
  const out: QuoteAction[] = [];
  if (s === "draft") out.push("send");
  if (live) out.push("resend");
  if ((s === "draft" || live) && !renewal) out.push("edit");
  if (s === "draft") out.push("viewPdf");
  if (hasLink) out.push("viewQuote", "copyLink");
  // History is offered at every status: a reopened draft carries the version Session APW's reopen wrote.
  out.push("history");
  out.push("duplicate");
  if (q.frequencyChangePending) out.push("reviewFrequency");
  if (live || s === "opened" || s === "accepted") out.push("void");
  if (LOSTABLE.has(s)) out.push("markLost");
  if (s === "lost" || s === "voided") out.push("reopen");
  if (live || s === "expired") out.push("extend");
  if (s === "expired" && !renewal) out.push("requote");
  if (opts.isAdmin) out.push("delete");
  return out;
}

// The ONE control a list row shows outside its "..." menu: the action that matters for the quote's
// status. Null where nothing is waiting on the office (a signed quote, an accepted one awaiting its
// signature, a legacy `opened` import), and the row then shows its menu alone, as the call-outs list does.
export function quoteRowPrimary(q: QuoteActionSubject, available: readonly QuoteAction[]): QuoteAction | null {
  const pick = (a: QuoteAction): QuoteAction | null => (available.includes(a) ? a : null);
  // A held frequency change blocks the agreement, so it outranks everything else on the row.
  if (q.frequencyChangePending) return pick("reviewFrequency");
  switch (q.status) {
    case "draft":
      return pick("send");
    case "pending":
    case "sent":
      return pick("resend");
    case "expired":
      return pick("extend");
    case "lost":
    case "voided":
      return pick("reopen");
    case "declined":
      return pick("markLost");
    default:
      return null;
  }
}

// The row's split: the primary, and everything else in the menu. Nothing is dropped; the menu is the
// full set minus the primary, which quoteActions.test.ts asserts for every status.
export function quoteRowActions(
  q: QuoteActionSubject,
  opts: { isAdmin: boolean },
): { primary: QuoteAction | null; menu: QuoteAction[]; all: QuoteAction[] } {
  const all = quoteActions(q, opts);
  const primary = quoteRowPrimary(q, all);
  return { primary, menu: all.filter((a) => a !== primary), all };
}

// Session AIE: shown on a Send / Resend control when the quote has no address to go to. It names BOTH
// places an address can come from, because the server resolves the quote's own contact email first
// and the linked customer's billing email second.
export const NO_RECIPIENT_HINT =
  "No email address to send to. Add a contact email on the quote, or a billing email on the customer record.";

// The armed label of the two-step Send / Resend confirm (the AEH branded-confirm rule: a send confirm
// NAMES its recipient). The address when the server resolved one, else the customer's name.
export function quoteSendConfirmLabel(
  verb: "Send" | "Resend",
  q: { recipientEmail?: string | null; customer?: { name: string } | null; customerName?: string | null },
): string {
  const who = q.recipientEmail || q.customer?.name || q.customerName || "the customer";
  return `${verb} to ${who}?`;
}

// Delete is FORCED for a quote that has rendered a contract: signed, or accepted (the ESA is out, so a
// draft contract exists). The server refuses an unforced delete of either with 409 has_contract; the
// forced delete cancels the contract and voids its Xero draft invoices. Admin only on both pages.
export function quoteDeleteIsForced(status: string): boolean {
  return status === "signed" || status === "accepted";
}

// The forced delete's modal copy, word for word what the list row has shown since Session AJ.
export const QUOTE_FORCED_DELETE_MESSAGE =
  "This quote has been signed into a contract. Deleting it permanently removes the quote, archives the associated contract and voids its Xero draft invoice. This cannot be undone.";
export const QUOTE_FORCED_DELETE_WARNING = "This quote is signed and tied to an active contract.";

// The router state the calculator's Resume flow reads (QuoteCalculatorPage), built from a quote the
// caller has just fetched with api.quotes.get. Both pages fetch first and then navigate with this, so
// Edit and Re-quote hand the calculator the same state from either place. The calculator autosaves a
// DRAFT resume but never an already-sent quote (status rides along, Session AAV); the stored payload
// lets it rebuild a quote with no calculatorState (AHP); portfolioId lets its live preview resolve the
// portfolio band (AIX).
export function quoteResumeState(q: QuoteDetail) {
  const calculatorState = (q.payload as { calculatorState?: unknown } | null | undefined)?.calculatorState;
  return {
    resume: {
      id: q.id,
      ref: q.quoteRef,
      status: q.status,
      calculatorState: calculatorState ?? undefined,
      payload: q.payload,
      portfolioId: q.portfolioId ?? null,
    },
  };
}
